import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { orderService, Order } from '../services/orderService';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const OrdersPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);

  useEffect(() => {
    if (user) setOrders(orderService.getOrdersForUser(user.id));
  }, [user]);

  const handleCancel = (orderId: string) => {
    if (!window.confirm('Cancel this order?')) return;
    const updated = orderService.cancelOrder(orderId);
    if (updated) {
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'cancelled' } : o));
    } else {
      alert('This order cannot be cancelled.');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FCFCFC] flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold mb-2">Please login to view your orders</h2>
          <button onClick={() => navigate('/login')} className="bg-[#DAA520] text-white px-6 py-2 rounded hover:bg-[#c5941c] mt-4">
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFCFC]">
      <div className="py-8 px-4 md:px-20">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 mb-8">Your Orders</h1>

          {orders.length === 0 ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <div className="text-6xl mb-4">📦</div>
              <h3 className="text-2xl font-semibold mb-3">No orders yet</h3>
              <p className="text-gray-600 mb-6">Start shopping to see your orders here.</p>
              <button onClick={() => navigate('/')} className="bg-[#DAA520] text-white px-8 py-3 rounded font-semibold hover:bg-[#c5941c]">
                Start Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map(order => (
                <div key={order.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                  {/* Header */}
                  <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">Order #{order.orderNumber}</h3>
                      <p className="text-gray-500 text-sm mt-1">Placed on {formatDate(order.createdAt)}</p>
                    </div>
                    <div className="flex flex-col md:items-end gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                      <p className="text-xl font-bold text-[#DAA520]">₹{order.totalAmount.toFixed(0)}</p>
                    </div>
                  </div>

                  {/* Items */}
                  <div className="p-6">
                    <h4 className="font-semibold text-gray-700 mb-4">Items ({order.items.length})</h4>
                    <div className="space-y-4">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4">
                          <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                            <img src={encodeURI(item.imageUrl)} alt={item.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-800">{item.name}</h5>
                            <p className="text-gray-500 text-sm">Qty: {item.quantity} × ₹{item.price.toFixed(0)}</p>
                            {item.selectedSize && <p className="text-gray-500 text-sm">Size: {item.selectedSize}</p>}
                          </div>
                          <p className="font-semibold text-gray-800">₹{(item.price * item.quantity).toFixed(0)}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-2">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Payment:</span> {order.paymentMethod.toUpperCase()} •{' '}
                      <span className={order.paymentStatus === 'completed' ? 'text-green-600' : 'text-yellow-600'}>
                        {order.paymentStatus}
                      </span>
                    </p>
                    <div className="flex items-center gap-4">
                      <p className="text-sm text-gray-500">
                        {order.shippingAddress.slice(0, 50)}{order.shippingAddress.length > 50 ? '…' : ''}
                      </p>
                      <button
                        onClick={() => navigate(`/orders/track/${order.id}`)}
                        className="text-sm text-[#DAA520] hover:text-[#c5941c] font-medium border border-[#DAA520] px-3 py-1 rounded-lg hover:bg-[#DAA520]/10 transition-colors"
                      >
                        Track Order
                      </button>
                      {['pending', 'confirmed'].includes(order.status) && (
                        <button
                          onClick={() => handleCancel(order.id)}
                          className="text-sm text-red-600 hover:text-red-800 font-medium border border-red-200 px-3 py-1 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          Cancel Order
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
